"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useUploadDocumentMutation } from "@/store/services/documents";
import { useGetAddressesListQuery } from "@/store/services/address";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

type UploadDocumentDialogProps = {
  open: boolean;
  onClose: () => void;
  preselectedAddressId?: number | null;
  lockAddressSelection?: boolean;
  onUploadedSuccess?: (payload: {
    addressId: number;
    fileNames: string[];
    documentIds: number[];
  }) => void;
  allowMultipleFiles?: boolean;
};

type FileWithType = {
  file: File;
  file_type: string;
};

const ALLOWED_FILE_EXTENSIONS = new Set([
  "pdf",
  "txt",
  "csv",
  "json",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "docx",
  "xlsx",
  "pptx",
  "doc",
  "xls",
  "ppt",
  "svg",
]);

const FILE_INPUT_ACCEPT = Array.from(ALLOWED_FILE_EXTENSIONS)
  .map((extension) => `.${extension}`)
  .join(",");

type Address = {
  id: number;
  name: string | null;
  location_name: string | null;
  type: string | null;
  is_enabled: number;
};

function UploadDocumentDialog({
  open,
  onClose,
  preselectedAddressId = null,
  lockAddressSelection = false,
  onUploadedSuccess,
  allowMultipleFiles = true,
}: UploadDocumentDialogProps) {
  const navigate = useNavigate();
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [currentStep, setCurrentStep] = useState<"address" | "upload">(
    "address"
  );
  const [files, setFiles] = useState<FileWithType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadDocument, { isLoading }] = useUploadDocumentMutation();
  const { data: addressesResponse } = useGetAddressesListQuery({});

  const addresses: Address[] = addressesResponse?.data || [];
  const enabledAddresses = useMemo(
    () => addresses.filter((address) => address.is_enabled !== 0),
    [addresses]
  );
  const canChangeAddress = enabledAddresses.length > 1;
  const MAX_FILES = allowMultipleFiles ? 10 : 1;

  // Auto-select address if only one exists and it is enabled
  useEffect(() => {
    if (open && preselectedAddressId) {
      const matchedAddress = addresses.find(
        (address: Address) =>
          address.id === preselectedAddressId && address.is_enabled !== 0
      );

      if (matchedAddress) {
        setSelectedAddressId(String(matchedAddress.id));
        setSelectedAddress(matchedAddress);
        setCurrentStep("upload");
        return;
      }
    }

    if (open && enabledAddresses.length === 1) {
      const address = enabledAddresses[0];
      setSelectedAddressId(String(address.id));
      setSelectedAddress(address);
      setCurrentStep("upload");
    } else if (open && enabledAddresses.length !== 1) {
      setCurrentStep("address");
      setSelectedAddressId("");
      setSelectedAddress(null);
    }
  }, [open, addresses, enabledAddresses, preselectedAddressId]);

  // const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
  // 	const selectedFiles = event.target.files;

  // 	if (!selectedFiles) return;

  // 	const currentFileCount = files.length;
  // 	const newFilesCount = selectedFiles.length;
  // 	const totalFiles = currentFileCount + newFilesCount;
  // 	console.log("totalFiles___",totalFiles)

  // 	if (totalFiles > MAX_FILES) {
  // 		toast.error(
  // 			`Maximum ${MAX_FILES} documents allowed. You can upload ${MAX_FILES - currentFileCount} more document(s).`
  // 		);
  // 		return;
  // 	}

  // 	const newFiles: FileWithType[] = Array.from(selectedFiles).map((file) => {
  // 		const extension = file.name.split('.').pop()?.toLowerCase() || '';
  // 		return {
  // 			file,
  // 			file_type: extension
  // 		};
  // 	});

  // 	setFiles((prev) => [...prev, ...newFiles]);
  // };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;

    if (!selectedFiles) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    const currentFileCount = files.length;
    const newFilesCount = selectedFiles.length;
    const totalFiles = currentFileCount + newFilesCount;

    if (totalFiles > MAX_FILES) {
      toast.error(
        `Maximum ${MAX_FILES} documents allowed. You can upload ${MAX_FILES - currentFileCount} more document(s).`
      );
      return;
    }

    // Check file sizes and allowed extensions
    const oversizedFiles: string[] = [];
    const invalidTypeFiles: string[] = [];
    const validFiles: File[] = [];

    Array.from(selectedFiles).forEach((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      const isAllowedType = ALLOWED_FILE_EXTENSIONS.has(extension);

      if (!isAllowedType) {
        invalidTypeFiles.push(file.name);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidTypeFiles.length > 0) {
      toast.error(
        `Unsupported file type: ${invalidTypeFiles.join(", ")}. Allowed types: ${Array.from(ALLOWED_FILE_EXTENSIONS).join(", ")}`
      );
    }

    if (oversizedFiles.length > 0) {
      const fileList = oversizedFiles.join(", ");
      toast.error(
        `File size should not be greater than 10MB. ${oversizedFiles.length > 1 ? "These files exceed" : "This file exceeds"} the limit: ${fileList}`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            zIndex: 9999999,
          },
        }
      );
    }

    // Only process valid files
    if (validFiles.length > 0) {
      const newFiles: FileWithType[] = validFiles.map((file) => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        return {
          file,
          file_type: extension,
        };
      });
      if (allowMultipleFiles) {
        setFiles((prev) => [...prev, ...newFiles]);
      } else {
        setFiles([newFiles[0]]);
      }
    }
  };
  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedAddressId) {
      toast.error("Please select an address");
      return;
    }

    if (files.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    try {
      const formData = new FormData();

      // Format: documents[0][address_id]: "6"
      formData.append("documents[0][address_id]", selectedAddressId);

      // Format: documents[0][documents][0][file_type]: "png", documents[0][documents][0][file]: file
      files.forEach((fileWithType, index) => {
        formData.append(
          `documents[0][documents][${index}][file_type]`,
          fileWithType.file_type
        );
        formData.append(
          `documents[0][documents][${index}][file]`,
          fileWithType.file
        );
      });

      // Send FormData directly as payload
      const response = await uploadDocument({ payload: formData });

      if (!response.error) {
        const uploadedDocuments =
          ((response as { data?: { data?: Array<{ id?: number }> } })?.data?.data || []);
        const uploadedIds = uploadedDocuments
          .map((item) => Number(item?.id))
          .filter((id) => Number.isFinite(id) && id > 0);

        toast.success("Documents uploaded successfully");
        onUploadedSuccess?.({
          addressId: Number(selectedAddressId),
          fileNames: files.map((f) => f.file.name),
          documentIds: uploadedIds,
        });
        handleClose();
      } else {
        toast.error(
          (response.error as { data?: { errors?: { detail?: string }[] } })
            ?.data?.errors?.[0]?.detail || "Failed to upload documents"
        );
      }
    } catch (error) {
      toast.error("An error occurred while uploading documents");
      console.error(error);
    }
  };

  const handleAddressSelect = (address: Address) => {
    setSelectedAddressId(String(address.id));
    setSelectedAddress(address);
    setCurrentStep("upload");
  };

  const handleBackToAddress = () => {
    setCurrentStep("address");
  };

  const handleClose = () => {
    setSelectedAddressId("");
    setSelectedAddress(null);
    setFiles([]);
    setCurrentStep("address");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onClose();
  };

  const getTypeLabel = (type: string | null) => {
    if (!type) return "N/A";

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getTypeColor = (type: string | null) => {
    if (!type) return "default";

    const typeMap: Record<string, "primary" | "success" | "warning"> = {
      apartment: "primary",
      house: "success",
      office: "warning",
      residential: "primary",
    };
    return typeMap[type] || "default";
  };

  const formatFileSizeInMb = (sizeInBytes: number) => {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalSelectedBytes = useMemo(() => {
    return files.reduce((sum, item) => sum + (item?.file?.size || 0), 0);
  }, [files]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentStep === "address" ? "Select Address" : "Upload Documents"}
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 mt-2">
          {/* Address Selection Step */}
          {currentStep === "address" && (
            <Box>
              <Typography variant="body1" className="mb-4 font-medium">
                Select an address to upload documents:
              </Typography>
              {addresses.length === 0 ? (
                <Box className="flex flex-col items-stretch gap-4 py-2">
                  <Alert severity="info">
                    There is no address yet. Please add an address first, then you
                    can upload documents.
                  </Alert>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      handleClose();
                      navigate("/apps/addresses");
                    }}
                    startIcon={
                      <FuseSvgIcon size={20}>
                        heroicons-outline:plus
                      </FuseSvgIcon>
                    }
                  >
                    Add Address
                  </Button>
                </Box>
              ) : (
                <Box className="grid grid-cols-2 gap-3">
                  {addresses.map((address: Address) => {
                    const isDisabled = address.is_enabled === 0;
                    return (
                    <Card
                      key={address.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        opacity: isDisabled ? 0.6 : 1,
                        cursor: isDisabled ? "not-allowed" : "default",
                        "&:hover": isDisabled
                          ? {}
                          : {
                              borderColor: "primary.main",
                              boxShadow: 2,
                            },
                      }}
                    >
                      <CardActionArea
                        onClick={() => !isDisabled && handleAddressSelect(address)}
                        disabled={isDisabled}
                      >
                        <CardContent>
                          <Box className="flex items-center justify-between">
                            <Box className="flex-1">
                              <Typography
                                variant="h6"
                                className="font-semibold mb-1 text-lg"
                              >
                                {address.name || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                className="text-gray-600 mb-2"
                              >
                                {address.location_name || "N/A"}
                              </Typography>
                              <Box className="flex items-center gap-2">
                                <Chip
                                  label={getTypeLabel(address.type)}
                                  color={getTypeColor(address.type)}
                                  size="small"
                                />
                                {isDisabled && (
                                  <Chip
                                    label="Disabled"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                            {!isDisabled && (
                              <FuseSvgIcon className="text-gray-400">
                                heroicons-outline:chevron-right
                              </FuseSvgIcon>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  )})}
                </Box>
              )}
            </Box>
          )}

          {/* File Upload Step */}
          {currentStep === "upload" && selectedAddress && (
            <Box>
              {/* Selected Address Display */}
              <Card
                sx={{
                  // backgroundColor: 'primary.light',
                  border: "1px solid",
                  borderColor: "primary.main",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Box className="flex items-center justify-between">
                    <Box>
                      <Typography
                        variant="caption"
                        className="text-primary font-medium uppercase underline"
                      >
                        Selected Address:
                      </Typography>
                      <Typography
                        variant="h6"
                        className="font-semibold mt-1 text-lg"
                      >
                        {selectedAddress.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        {selectedAddress.location_name || "N/A"}
                      </Typography>
                    </Box>
                    {canChangeAddress && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleBackToAddress}
                        disabled={isLoading || lockAddressSelection}
                      >
                        Change
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Box>
                {files.length === 0 && (
                  <Box className="flex items-center justify-between mb-2">
                    <Typography variant="body2" className="font-medium">
                      Select Files to Upload
                    </Typography>
                    <Typography
                      variant="caption"
                      className={
                        files.length >= MAX_FILES ? "text-error" : "text-gray-500"
                      }
                    >
                      {files.length}/{MAX_FILES}
                    </Typography>
                  </Box>
                )}
                <input
                  title="Upload Files"
                  placeholder="Upload Files"
                  ref={fileInputRef}
                  type="file"
                  multiple={allowMultipleFiles}
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept={FILE_INPUT_ACCEPT}
                />
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || files.length >= MAX_FILES}
                  startIcon={
                    <FuseSvgIcon>heroicons-outline:paper-clip</FuseSvgIcon>
                  }
                  className="w-full"
                >
                  {files.length > 0 ? "Add More Files" : "Upload Files"}
                </Button>
                {files.length >= MAX_FILES && (
                  <Alert severity="info" className="mt-2">
                    Maximum {MAX_FILES} documents allowed. Please remove some
                    files to add more.
                  </Alert>
                )}

                {/* Selected Files List */}
                {files.length > 0 && (
                  <Box className="mt-4">
                    <Box className="mb-3 rounded-md border px-3 py-2 bg-gray-50">
                      <Box className="flex items-center justify-between">
                        <Typography variant="body2" className="font-medium text-gray-700">
                          Total size
                        </Typography>
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          label={`${formatFileSizeInMb(totalSelectedBytes)} • ${files.length} file${
                            files.length !== 1 ? "s" : ""
                          }`}
                        />
                      </Box>
                    </Box>
                    <Box className="mb-1 flex items-center justify-between">
                      <Typography variant="body2" className="font-medium">
                        Selected Files ({files.length}/{MAX_FILES}):
                      </Typography>
                      {files.length > 1 &&(
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setFiles([])}
                          disabled={isLoading}
                          sx={{
                            minWidth: "auto",
                            p: 0,
                            color: "error.main",
                            textDecoration: "underline",
                            "&:hover": {
                              textDecoration: "underline",
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          Remove All Files
                        </Button>
                      )}
                    </Box>
                    <Box className="flex flex-col gap-2">
                      {files.map((fileWithType, index) => (
                        <Box
                          key={index}
                          className="flex items-center justify-between p-2 border rounded"
                        >
                          <Box className="flex items-center gap-2">
                            <Chip
                              label={fileWithType.file_type.toUpperCase()}
                              size="small"
                            />
                            <Typography
                              variant="body2"
                              className="truncate max-w-xs"
                            >
                              {fileWithType.file.name}
                            </Typography>
                          </Box>
                          <Box className="flex items-center justify-center gap-1">
                          <Typography
                            variant="caption"
                            className="text-gray-900"
                          >{formatFileSizeInMb(fileWithType.file.size)}</Typography>
                            <Tooltip title="Remove File">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(index)}
                                disabled={isLoading}
                              >
                                <FuseSvgIcon>heroicons-outline:x-mark</FuseSvgIcon>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button
          onClick={
            currentStep === "address"
              ? handleClose
              : lockAddressSelection
                ? handleClose
                : handleClose
          }
          disabled={isLoading}
        >
          {currentStep === "address"
            ? "Cancel"
            : lockAddressSelection
              ? "Cancel"
              : "Cancel"}
        </Button>
        {currentStep === "upload" && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            disabled={isLoading || !selectedAddressId || files.length === 0}
            startIcon={isLoading && <CircularProgress size={15} />}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UploadDocumentDialog;
